import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from '../config/api';
import { errorMessage } from "../utils/errorMessage";
import { useBonsai } from "../contexts/BonsaiContext";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
  LineChart,
  Line,
  Area,
  AreaChart,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Users,
  FileCheck,
  AlertTriangle,
  Clock,
  ArrowRight,
  RefreshCw,
  Calendar,
  ChevronLeft,
  ChevronDown,
  Filter,
  DollarSign,
  Timer,
  Target,
  Zap,
  Activity,
  Trash2,
  Info,
  MessageSquare,
  Send,
  X,
  Loader2,
  FolderOpen,
  Inbox,
  BookOpen,
  CheckCircle2,
  XCircle,
  Shield,
  Upload,
  FileText,
  ArrowDown,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fmtUK } from "../lib/dateUtils";
import { toast } from "sonner";
import { InfoTooltip } from "../components/InfoTooltip";
import VouchingForID from "../components/VouchingForID";

// ============ CONCERTINA (Collapsible Section) ============
function Concertina({ id, icon: Icon, iconColor = "text-teal", title, badge, children, defaultOpen = false, borderColor }) {
  const storageKey = `concertina_${id}`;
  const [open, setOpen] = useState(() => {
    try { const v = localStorage.getItem(storageKey); return v !== null ? v === "true" : defaultOpen; } catch { return defaultOpen; }
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(storageKey, String(next)); } catch {}
  };
  return (
    <div className={`rounded-lg border ${borderColor || "border-navy-border"} bg-navy overflow-hidden transition-all`} data-testid={`concertina-${id}`}>
      <button onClick={toggle} className="w-full flex items-center justify-between p-4 hover:bg-navy-hover/30 transition-colors" data-testid={`concertina-toggle-${id}`}>
        <div className="flex items-center gap-2.5">
          {Icon && <Icon className={`w-4 h-4 ${iconColor}`} />}
          <h3 className="text-sm font-semibold text-txt">{title}</h3>
          {badge}
        </div>
        <ChevronDown className={`w-4 h-4 text-txt-muted transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 pb-4 pt-0">{children}</div>}
    </div>
  );
}

// ============ FILING AUDIT LOG ============
function FilingAuditLog() {
  const [filings, setFilings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const limit = 30;

  const fetchFilings = useCallback(async (skip = 0) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bonsai/filing/audit-log?limit=${limit}&skip=${skip}`);
      setFilings(res.data?.filings || []);
      setTotal(res.data?.total || 0);
    } catch { setFilings([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchFilings(page * limit); }, [page, fetchFilings]);

  const fmtTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  if (loading && filings.length === 0) return <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-teal" /></div>;

  return (
    <div className="space-y-2" data-testid="filing-audit-log">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] text-txt-muted">{total} total filings</p>
        <div className="flex gap-1">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
            className="px-2 py-0.5 text-[10px] rounded border border-navy-border text-txt-muted hover:text-white disabled:opacity-30">Prev</button>
          <span className="text-[10px] text-txt-muted px-2 py-0.5">{page + 1} / {Math.ceil(total / limit) || 1}</span>
          <button disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)}
            className="px-2 py-0.5 text-[10px] rounded border border-navy-border text-txt-muted hover:text-white disabled:opacity-30">Next</button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="filing-audit-table">
          <thead>
            <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-txt-muted border-b border-navy-border">
              <th className="p-2">When</th>
              <th className="p-2">Who</th>
              <th className="p-2">Subject</th>
              <th className="p-2">Destination</th>
              <th className="p-2">Type</th>
            </tr>
          </thead>
          <tbody>
            {filings.map((f, i) => (
              <tr key={i} className="border-b border-navy-border/30 hover:bg-navy-hover/30 transition-colors">
                <td className="p-2 text-txt-muted whitespace-nowrap">{fmtTime(f.filed_at)}</td>
                <td className="p-2 text-white font-medium whitespace-nowrap">{(f.filed_by_id || "").split("@")[0]}</td>
                <td className="p-2 text-txt-muted max-w-[200px] truncate" title={f.subject}>{f.subject || "—"}</td>
                <td className="p-2 text-teal max-w-[180px] truncate" title={f.folder_name || f.matter_reference || f.sharepoint_path}>
                  {f.matter_reference || f.folder_name || "—"}
                </td>
                <td className="p-2">
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border
                    ${f.filing_type === "conversation" ? "text-blue-300 border-blue-500/30 bg-blue-500/10" :
                      f.filing_type === "batch" ? "text-purple-300 border-purple-500/30 bg-purple-500/10" :
                      "text-txt-muted border-navy-border bg-navy-hover/50"}`}>
                    {f.filing_type || "single"}
                  </span>
                </td>
              </tr>
            ))}
            {filings.length === 0 && !loading && (
              <tr><td colSpan={5} className="p-6 text-center text-txt-muted">No filing records found</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {loading && <div className="flex justify-center py-2"><Loader2 className="w-3 h-3 animate-spin text-txt-muted" /></div>}
    </div>
  );
}

const COLORS = {
  primary: "#4f46e5",
  secondary: "#1e293b",
  accent: "#F59E0B",
  danger: "#EF4444",
  muted: "#64748B",
  success: "#10B981",
  billing: "#6366F1",
};

const DATE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 Days" },
  { value: "30d", label: "Last 30 Days" },
  { value: "90d", label: "Last 90 Days" },
  { value: "year", label: "This Year" },
];

const DAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function CompactCompletionsCalendar({ data, view, onViewChange, onDayClick }) {
  // Build lookup
  const dayMap = {};
  let maxCount = 0;
  (data || []).forEach(d => {
    dayMap[d.date] = d;
    if (d.count > maxCount) maxCount = d.count;
  });

  // Generate upcoming days
  const now = new Date();
  const daysToShow = view === "week" ? 7 : 28;
  const days = [];
  for (let i = 0; i < daysToShow; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() + i);
    const dateStr = d.toISOString().split("T")[0];
    const dayOfWeek = (d.getDay() + 6) % 7; // Mon=0
    days.push({
      date: dateStr,
      dayNum: d.getDate(),
      dow: dayOfWeek,
      dowLabel: DAYS_SHORT[dayOfWeek],
      monthLabel: d.toLocaleDateString("en-GB", { month: "short" }),
      isToday: i === 0,
      isWeekend: dayOfWeek >= 5,
      data: dayMap[dateStr] || null,
    });
  }

  const totalUpcoming = days.reduce((sum, d) => sum + (d.data?.count || 0), 0);

  const getColor = (count) => {
    if (!count) return "bg-navy-hover border-navy-border";
    const ratio = count / Math.max(maxCount, 1);
    if (ratio <= 0.33) return "bg-purple-900/40 border-purple-700/40";
    if (ratio <= 0.66) return "bg-purple-700/50 border-purple-500/40";
    return "bg-purple-500/60 border-purple-400/50";
  };

  if (view === "week") {
    return (
      <div className="glass-card p-4" data-testid="cb-compact-calendar">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-purple-300" />
            <span className="text-xs font-bold text-txt-muted uppercase tracking-wider">Next 7 Days</span>
            {totalUpcoming > 0 && (
              <span className="text-[10px] font-bold text-purple-300 bg-purple-500/15 border border-purple-500/25 rounded px-1.5 py-0.5">{totalUpcoming} upcoming</span>
            )}
          </div>
          <div className="flex border border-navy-border rounded-md overflow-hidden">
            <button onClick={() => onViewChange("week")} className={`px-2 py-0.5 text-[10px] font-semibold ${view === "week" ? "bg-purple-500/20 text-purple-300" : "text-txt-muted hover:text-white"}`} data-testid="cb-cal-view-week">Week</button>
            <button onClick={() => onViewChange("month")} className={`px-2 py-0.5 text-[10px] font-semibold ${view === "month" ? "bg-purple-500/20 text-purple-300" : "text-txt-muted hover:text-white"}`} data-testid="cb-cal-view-month">Month</button>
          </div>
        </div>
        <div className="flex gap-1.5">
          {days.map(d => {
            const count = d.data?.count || 0;
            return (
              <button
                key={d.date}
                onClick={() => count > 0 && onDayClick(d.date)}
                className={`flex-1 rounded border p-2 text-center transition-all ${getColor(count)} ${count > 0 ? "cursor-pointer hover:ring-1 hover:ring-purple-400" : ""} ${d.isToday ? "ring-1 ring-teal" : ""}`}
                title={count > 0 ? `${d.date}: ${count} completion${count !== 1 ? "s" : ""} (${d.data.fee_earners?.join(", ")})` : d.date}
                data-testid={`cb-cal-day-${d.date}`}
              >
                <p className="text-[9px] text-txt-muted">{d.dowLabel}</p>
                <p className={`text-sm font-bold ${count > 0 ? "text-white" : d.isWeekend ? "text-txt-faint" : "text-txt-muted"}`}>{d.dayNum}</p>
                {count > 0 && <p className="text-[10px] font-bold text-purple-300">{count}</p>}
                {count === 0 && <p className="text-[10px] text-transparent">-</p>}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Month view — 4 weeks as grid
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));

  return (
    <div className="glass-card p-4" data-testid="cb-compact-calendar">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5 text-purple-300" />
          <span className="text-xs font-bold text-txt-muted uppercase tracking-wider">Next 4 Weeks</span>
          {totalUpcoming > 0 && (
            <span className="text-[10px] font-bold text-purple-300 bg-purple-500/15 border border-purple-500/25 rounded px-1.5 py-0.5">{totalUpcoming} upcoming</span>
          )}
        </div>
        <div className="flex border border-navy-border rounded-md overflow-hidden">
          <button onClick={() => onViewChange("week")} className={`px-2 py-0.5 text-[10px] font-semibold ${view === "week" ? "bg-purple-500/20 text-purple-300" : "text-txt-muted hover:text-white"}`} data-testid="cb-cal-view-week">Week</button>
          <button onClick={() => onViewChange("month")} className={`px-2 py-0.5 text-[10px] font-semibold ${view === "month" ? "bg-purple-500/20 text-purple-300" : "text-txt-muted hover:text-white"}`} data-testid="cb-cal-view-month">Month</button>
        </div>
      </div>
      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAYS_SHORT.map(d => <span key={d} className="text-center text-[8px] text-txt-muted font-medium">{d}</span>)}
      </div>
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7 gap-1 mb-1">
          {week.map(d => {
            const count = d.data?.count || 0;
            return (
              <button
                key={d.date}
                onClick={() => count > 0 && onDayClick(d.date)}
                className={`rounded border p-1 text-center transition-all ${getColor(count)} ${count > 0 ? "cursor-pointer hover:ring-1 hover:ring-purple-400" : ""} ${d.isToday ? "ring-1 ring-teal" : ""}`}
                title={count > 0 ? `${d.date}: ${count} completion${count !== 1 ? "s" : ""} (${d.data.fee_earners?.join(", ")})` : d.date}
                data-testid={`cb-cal-day-${d.date}`}
              >
                <p className={`text-[10px] font-medium ${count > 0 ? "text-white" : d.isWeekend ? "text-txt-faint" : "text-txt-muted"}`}>{d.dayNum}</p>
                {count > 0 && <p className="text-[9px] font-bold text-purple-300">{count}</p>}
              </button>
            );
          })}
        </div>
      ))}
      {/* Legend */}
      <div className="flex items-center justify-end gap-2 mt-2 text-[9px] text-txt-muted">
        <span>Less</span>
        <div className="flex gap-0.5">
          {["bg-navy-hover", "bg-purple-900/40", "bg-purple-700/50", "bg-purple-500/60"].map((cls, i) => (
            <div key={i} className={`w-2.5 h-2.5 rounded-sm ${cls}`} />
          ))}
        </div>
        <span>More</span>
      </div>
    </div>
  );
}

function FeeEarnerStatsSection() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/completionsbook/fee-earner-stats`);
        setStats(res.data.stats || []);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="glass-card p-4 text-center"><Loader2 className="w-4 h-4 animate-spin text-teal mx-auto" /></div>;
  if (!stats.length) return null;

  return (
    <div className="glass-card p-4" data-testid="fe-stats-section">
      <h4 className="text-xs font-bold text-txt-muted mb-3 uppercase tracking-wider flex items-center gap-1.5">
        <Users className="w-3.5 h-3.5 text-teal" /> Fee Earner Statistics
      </h4>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-txt-muted border-b border-navy-border">
              <th className="p-2">Fee Earner</th>
              <th className="p-2 text-center">Total</th>
              <th className="p-2 text-center">Completed</th>
              <th className="p-2 text-center">Rate</th>
              <th className="p-2 text-center">Issues</th>
              <th className="p-2">Top Type</th>
              <th className="p-2">Progress</th>
            </tr>
          </thead>
          <tbody>
            {stats.map(s => (
              <tr key={s.fee_earner} className="border-b border-navy-border/30 hover:bg-navy-hover/30 transition-colors" data-testid={`fe-stat-row-${s.fee_earner}`}>
                <td className="p-2 font-medium text-white">{s.fee_earner || "—"}</td>
                <td className="p-2 text-center text-txt">{s.total}</td>
                <td className="p-2 text-center text-teal">{s.completed}</td>
                <td className="p-2 text-center text-white font-semibold">{s.completion_rate}%</td>
                <td className="p-2 text-center">
                  <span className={s.with_issues > 0 ? "text-status-red" : "text-txt-muted"}>{s.with_issues}</span>
                </td>
                <td className="p-2 text-txt-muted">{s.top_type}</td>
                <td className="p-2 w-24">
                  <div className="w-full bg-navy-card rounded-full h-1.5">
                    <div className="bg-teal h-1.5 rounded-full transition-all" style={{ width: `${s.completion_rate}%` }} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function DirectorAnalytics() {
  const navigate = useNavigate();

  // Brief 35 — page-level director guard. Soft-fails closed: any
  // non-director or fetch error redirects home.
  useEffect(() => {
    const currentUserEmail =
      sessionStorage.getItem("bonsai_user_id") ||
      localStorage.getItem("bonsai_user_id") ||
      "";
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/user-role`, {
          headers: { "X-Bonsai-User-Id": currentUserEmail },
        });
        if (cancelled) return;
        if (!res.data?.is_director) {
          toast.error("Director access only");
          navigate("/");
        }
      } catch {
        if (!cancelled) {
          toast.error("Director access only");
          navigate("/");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const { user_email: bonsaiEmail } = useBonsai();
  const [loading, setLoading] = useState(true);
  const [datePreset, setDatePreset] = useState("30d");
  const [summary, setSummary] = useState(null);
  const [leagueTable, setLeagueTable] = useState([]);
  const [riskSignals, setRiskSignals] = useState(null);
  const [feeEarners, setFeeEarners] = useState([]);
  const [selectedFeeEarner, setSelectedFeeEarner] = useState("all");
  const [billingSummary, setBillingSummary] = useState(null);
  const [utilizationData, setUtilizationData] = useState([]);
  const [lawbillityEnabled, setLawbillityEnabled] = useState(false);
  const [clientExperience, setClientExperience] = useState(null);
  const [experienceTrend, setExperienceTrend] = useState([]);
  const [entitySegments, setEntitySegments] = useState([]);
  
  // Backfill state
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  // Stale matters state
  const [staleMatters, setStaleMatters] = useState(null);
  const [staleLoading, setStaleLoading] = useState(false);
  const [staleSending, setStaleSending] = useState(false);
  const [directors, setDirectors] = useState({});

  useEffect(() => {
    axios.get(`${API}/staff`).then(res => setDirectors(res.data.directors || {})).catch(() => {});
  }, []);
  // Resolve director email: prefer BonsaiContext (from MS SSO auth status),
  // then localStorage user object, then storage fallbacks
  const directorEmail = (() => {
    // 1. BonsaiContext user_email (resolved from session token via /api/bonsai/auth/status)
    if (bonsaiEmail && bonsaiEmail.includes("@")) return bonsaiEmail;
    // 2. user object stored by LoginPage on email/password login
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      if (u.email && u.email.includes("@")) return u.email;
    } catch {}
    // 3. bonsai_user_email (actual email, preferred over bonsai_user_id which is a UUID)
    const ue = sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email");
    if (ue && ue.includes("@")) return ue;
    // 4. localStorage bonsai_user_id (only if it looks like an email)
    const ls = localStorage.getItem("bonsai_user_id");
    if (ls && ls.includes("@")) return ls;
    // 5. sessionStorage (only if it looks like an email)
    const ss = sessionStorage.getItem("bonsai_user_id");
    if (ss && ss.includes("@")) return ss;
    return "";
  })();
  
  // Purge test data state (BM-H286: preview first, then delete)
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const [purgePreview, setPurgePreview] = useState(null);
  const [purgeIncludePatterns, setPurgeIncludePatterns] = useState(false);

  // Post stats state
  const [postStats, setPostStats] = useState(null);
  
  // Conveyancing completions state
  const [cbMetrics, setCbMetrics] = useState(null);
  const [cbLoading, setCbLoading] = useState(false);
  const [cbRange, setCbRange] = useState("month");
  
  // Upcoming completions calendar state
  const [cbCalendar, setCbCalendar] = useState([]);
  const [cbCalView, setCbCalView] = useState("week"); // "week" or "month"

  // Completions Book import state
  const [cbImporting, setCbImporting] = useState(false);
  const [cbImportResult, setCbImportResult] = useState(null);
  const cbFileRef = useRef(null);
  
  // AI Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { role: "assistant", content: "Hello! I can help you understand your analytics data. Ask me about conversion rates, ageing patterns, fee earner performance, or any metric you see here." }
  ]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);

  // Stale matters handlers
  const fetchStaleMatters = async () => {
    setStaleLoading(true);
    try {
      const userId = sessionStorage.getItem('bonsai_user_id') || localStorage.getItem('bonsai_user_id') || '';
      const res = await axios.get(`${API}/hub/stale-nudge/preview`, { headers: { 'X-Bonsai-User-Id': userId } });
      setStaleMatters(res.data);
    } catch (e) {
      toast.error('Failed to load stale matters');
    } finally {
      setStaleLoading(false);
    }
  };

  const triggerStaleNudges = async () => {
    setStaleSending(true);
    try {
      const userId = sessionStorage.getItem('bonsai_user_id') || localStorage.getItem('bonsai_user_id') || '';
      const res = await axios.post(`${API}/hub/stale-nudge/run`, {}, { headers: { 'X-Bonsai-User-Id': userId } });
      const r = res.data.results;
      toast.success(`Nudges sent: ${r.nudged} emailed, ${r.skipped} skipped, ${r.errors} errors`);
      fetchStaleMatters();
    } catch (e) {
      toast.error('Failed to send nudges');
    } finally {
      setStaleSending(false);
    }
  };

  // Backfill handlers
  const fetchBackfillLog = async () => {
    try {
      const res = await axios.get(`${API}/hub/compliance/backfill-log`);
      if (res.data?.timestamp) setBackfillResult(res.data);
    } catch {}
  };

  const fetchPostStats = async () => {
    try {
      const res = await axios.get(`${API}/postintake/stats`);
      setPostStats(res.data);
    } catch {}
  };

  const fetchCBMetrics = async (range) => {
    setCbLoading(true);
    const now = new Date();
    let dateFrom = "", dateTo = "";
    if (range === "today") {
      dateFrom = dateTo = now.toISOString().split("T")[0];
    } else if (range === "week") {
      const d = new Date(now); d.setDate(d.getDate() - d.getDay() + 1);
      dateFrom = d.toISOString().split("T")[0];
      dateTo = "2099-12-31";
    } else if (range === "month") {
      dateFrom = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`;
      dateTo = "2099-12-31";
    } else if (range === "lastmonth") {
      const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      dateFrom = lm.toISOString().split("T")[0];
      dateTo = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split("T")[0];
    } else if (range === "year") {
      dateFrom = `${now.getFullYear()}-01-01`;
      dateTo = "2099-12-31";
    }
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo && dateTo !== "2099-12-31") params.date_to = dateTo;
      const res = await axios.get(`${API}/metrics/conveyancing-completions`, { params });
      setCbMetrics(res.data);
    } catch {}
    setCbLoading(false);
  };

  const fetchCBCalendar = async () => {
    try {
      const now = new Date();
      const dateFrom = now.toISOString().split("T")[0];
      // Fetch 5 weeks ahead
      const end = new Date(now);
      end.setDate(end.getDate() + 35);
      const dateTo = end.toISOString().split("T")[0];
      const res = await axios.get(`${API}/completionsbook/calendar`, { params: { date_from: dateFrom, date_to: dateTo } });
      setCbCalendar(res.data.days || []);
    } catch {}
  };

  const handleCBImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCbImporting(true);
    setCbImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/completionsbook/import`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCbImportResult({ success: true, imported: res.data.imported, skipped: res.data.skipped });
      toast.success(`Completions Book imported: ${res.data.imported} rows`);
      fetchCBMetrics(cbRange);
      fetchCBCalendar();
    } catch (err) {
      const msg = errorMessage(err, "Import failed");
      setCbImportResult({ success: false, error: msg });
      toast.error(`Import failed: ${msg}`);
    } finally {
      setCbImporting(false);
      if (cbFileRef.current) cbFileRef.current.value = "";
    }
  };

  const handleBackfillUnfiled = async () => {
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const res = await axios.post(`${API}/compliance/backfill-unfiled`);
      setBackfillResult(res.data);
      const { succeeded, failed, processed } = res.data;
      toast.success(`Backfill complete: ${succeeded}/${processed} filed successfully`);
    } catch (err) {
      toast.error(errorMessage(err, "Backfill failed"));
    } finally {
      setBackfilling(false);
    }
  };

  // BM-H286. Two steps, deliberately. Previously one click bulk
  // hard-deleted four collections behind a window.confirm that did not
  // say what it was about to destroy.
  // Samples come back keyed by whichever field the record actually carries.
  // Show the director a name or a reference, not an opaque id.
  const purgeSampleLabel = (row) => {
    const entries = Object.entries(row || {}).filter(([, v]) => v !== null && v !== undefined && v !== "");
    const named = entries.find(([k]) => !/(^|_)id$/i.test(k));
    return (named || entries[0] || [null, null])[1];
  };

  const purgeTotals = (preview) =>
    Object.values(preview || {}).reduce(
      (acc, c) => ({
        flagged: acc.flagged + (c.flagged || 0),
        patternOnly: acc.patternOnly + (c.pattern_only || 0),
      }),
      { flagged: 0, patternOnly: 0 }
    );

  const handlePreviewTestData = async () => {
    setPurging(true);
    setPurgeResult(null);
    try {
      const res = await axios.post(`${API}/matters/purge-test-data`, null, {
        params: { dry_run: true, include_pattern_matches: purgeIncludePatterns },
      });
      setPurgePreview(res.data.would_delete || null);
      const { flagged, patternOnly } = purgeTotals(res.data.would_delete);
      toast.success(
        `Preview: ${flagged} flagged test record${flagged === 1 ? "" : "s"} would be purged` +
          (patternOnly ? `, ${patternOnly} pattern-only match${patternOnly === 1 ? "" : "es"} found` : "")
      );
    } catch (err) {
      toast.error(errorMessage(err, "Preview failed"));
    } finally {
      setPurging(false);
    }
  };

  const handlePurgeTestData = async () => {
    const { flagged, patternOnly } = purgeTotals(purgePreview);
    if (!flagged && !(purgeIncludePatterns && patternOnly)) {
      toast.error("Nothing to purge. Run the preview first.");
      return;
    }
    const lines = [
      `Permanently delete ${flagged} flagged test record${flagged === 1 ? "" : "s"}?`,
      ...Object.entries(purgePreview || {})
        .filter(([, c]) => c.flagged)
        .map(([name, c]) => `  ${name.replace(/_/g, " ")}: ${c.flagged}`),
    ];
    if (purgeIncludePatterns && patternOnly) {
      lines.push(`Plus ${patternOnly} pattern-only match${patternOnly === 1 ? "" : "es"}, which are marked deleted, not destroyed.`);
    }
    lines.push("Conflict checks, onboarding records and test quotes are removed permanently. This cannot be undone.");
    if (!window.confirm(lines.join("\n"))) return;

    setPurging(true);
    try {
      const res = await axios.post(`${API}/matters/purge-test-data`, null, {
        params: { dry_run: false, include_pattern_matches: purgeIncludePatterns },
      });
      setPurgeResult(res.data);
      setPurgePreview(res.data.would_delete || null);
      const {
        submissions_soft_deleted = 0,
        conflict_checks_deleted = 0,
        onboarding_deleted = 0,
        quotes_deleted = 0,
      } = res.data;
      toast.success(
        `Purged: ${submissions_soft_deleted} submissions, ${conflict_checks_deleted} conflict checks, ` +
          `${onboarding_deleted} onboarding records, ${quotes_deleted} test quotes`
      );
    } catch (err) {
      toast.error(errorMessage(err, "Purge failed"));
    } finally {
      setPurging(false);
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMessage = chatInput.trim();
    setChatInput("");
    setChatMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setChatLoading(true);
    
    try {
      const res = await axios.post(`${API}/analytics/chat`, {
        question: userMessage,
        context: summary ? {
          funnel: summary.funnel,
          ageing: summary.ageing,
          forf_ready: summary.forf_ready,
          date_range: summary.date_range,
        } : null
      });
      
      if (res.data.success) {
        setChatMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
      } else {
        setChatMessages(prev => [...prev, { role: "assistant", content: res.data.response || "Sorry, I couldn't process that question." }]);
      }
    } catch (error) {
      setChatMessages(prev => [...prev, { role: "assistant", content: "Sorry, something went wrong. Please try again." }]);
    } finally {
      setChatLoading(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, leagueRes, riskRes, configRes, billingRes, utilizationRes, expRes, trendRes, segmentRes] = await Promise.all([
        axios.get(`${API}/analytics/summary?preset=${datePreset}`),
        axios.get(`${API}/analytics/fee-earner-league?preset=${datePreset}`),
        axios.get(`${API}/analytics/risk-signals?preset=${datePreset}`),
        axios.get(`${API}/analytics/config/fee-earners`),
        axios.get(`${API}/analytics/billing/summary?preset=${datePreset}`).catch(() => ({ data: { enabled: false } })),
        axios.get(`${API}/analytics/billing/utilization?preset=${datePreset}`).catch(() => ({ data: { enabled: false, utilization: [] } })),
        axios.get(`${API}/analytics/client-experience?preset=${datePreset}`).catch(() => ({ data: {} })),
        axios.get(`${API}/analytics/client-experience/trend?months=6`).catch(() => ({ data: { trend: [] } })),
        axios.get(`${API}/analytics/client-experience/by-segment?segment=entity_type&preset=${datePreset}`).catch(() => ({ data: { segments: [] } })),
      ]);
      setSummary(summaryRes.data);
      setLeagueTable(leagueRes.data.league_table || []);
      setRiskSignals(riskRes.data);
      setFeeEarners(configRes.data.fee_earners || []);
      setBillingSummary(billingRes.data);
      setUtilizationData(utilizationRes.data.utilization || []);
      setLawbillityEnabled(billingRes.data.enabled || false);
      setClientExperience(expRes.data);
      setExperienceTrend(trendRes.data.trend || []);
      setEntitySegments(segmentRes.data.segments || []);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  }, [datePreset]);

  useEffect(() => {
    fetchData();
    fetchBackfillLog();
    fetchPostStats();
    fetchCBMetrics(cbRange);
    fetchCBCalendar();
  }, [fetchData]);

  useEffect(() => {
    fetchCBMetrics(cbRange);
  }, [cbRange]);

  const formatDate = (dateStr) => fmtUK(dateStr, "dd MMM yyyy");

  // Drill-down navigation helpers
  const navigateToOnboarding = (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.set("status", filters.status);
    if (filters.fee_earner) params.set("fee_earner", filters.fee_earner);
    if (filters.ids) params.set("ids", filters.ids.join(","));
    navigate(`/dashboard?tab=onboarding${params.toString() ? "&" + params.toString() : ""}`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Funnel data for chart
  const funnelData = summary
    ? [
        { name: "Conflicts Cleared", value: summary.funnel.conflicts_cleared, fill: COLORS.primary },
        { name: "OB Complete", value: summary.funnel.onboarding_complete, fill: COLORS.success },
        { name: "Matters Created", value: summary.funnel.matters_created, fill: COLORS.secondary },
      ]
    : [];

  // Ageing data for chart
  const ageingData = summary?.ageing?.buckets || [];

  // FORF ready data for pie chart
  const forfData = summary
    ? [
        { name: "FORF Ready", value: summary.forf_ready.ready, fill: COLORS.success },
        { name: "Not Ready", value: summary.forf_ready.not_ready, fill: COLORS.muted },
      ]
    : [];

  return (
    <div className="min-h-screen bg-navy-hover/60">
      {/* Header */}
      <div className="glass-nav sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-txt-muted hover:text-teal gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                Dashboard
              </Button>
              <div>
                <button onClick={() => window.location.href="/"} className="mb-3 text-xs text-teal hover:text-teal flex items-center gap-1">← Dashboard</button>
        <h1 className="text-xl font-bold text-white">Directors Lounge</h1>
                <p className="text-sm text-txt-muted">Onboarding Pipeline & Performance</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={datePreset} onValueChange={setDatePreset}>
                <SelectTrigger className="w-40" data-testid="date-preset-filter">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_PRESETS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={fetchData} className="gap-1">
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/bonsai/deleted-submissions")}
                className="gap-1 border-red-200 text-status-red hover:bg-status-red-dim"
                data-testid="deleted-submissions-link"
              >
                <Trash2 className="w-4 h-4" />
                Deletion Audit
              </Button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block w-8 h-8 border-2 border-teal border-t-transparent rounded-md animate-spin" />
            <p className="mt-4 text-slate-500">Loading analytics...</p>
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
          {/* COLP/COFA Vouching for ID */}
          <VouchingForID
            directorEmail={directorEmail}
            directorName={directors[directorEmail] || directorEmail}
          />

          {/* 19.x.1 Funnel KPIs */}
          <Concertina id="funnel-kpis" icon={FileCheck} title="Onboarding Funnel" defaultOpen={true}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div
              className="glass-card p-5 hover:border-teal/50 transition-colors cursor-pointer group"
              onClick={() => navigate("/conflict-check")}
              data-testid="funnel-conflicts"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-500">Conflicts Cleared</span>
                  <InfoTooltip metric="conflicts_cleared" />
                </div>
                <FileCheck className="w-5 h-5 text-teal" />
              </div>
              <p className="text-3xl font-bold text-white">{summary?.funnel?.conflicts_cleared || 0}</p>
              <p className="text-xs text-slate-400 mt-1 group-hover:text-teal">Click to view → </p>
            </div>
            <div
              className="glass-card p-5 hover:border-teal/50 transition-colors cursor-pointer group"
              onClick={() => navigateToOnboarding({ status: "complete" })}
              data-testid="funnel-onboarding"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-500">OB Complete</span>
                  <InfoTooltip metric="ob_complete" />
                </div>
                <Users className="w-5 h-5 text-teal" />
              </div>
              <p className="text-3xl font-bold text-white">{summary?.funnel?.onboarding_complete || 0}</p>
              <div className="flex items-center gap-1 mt-1">
                {summary?.funnel?.conversion_conflict_to_ob > 100 ? (
                  <TrendingUp className="w-3 h-3 text-teal" />
                ) : (
                  <TrendingDown className="w-3 h-3 text-status-amber" />
                )}
                <span className="text-xs text-slate-500 group-hover:text-teal">
                  {summary?.funnel?.conversion_conflict_to_ob || 0}% from conflicts →
                </span>
                <InfoTooltip metric="conversion_cc_ob" />
              </div>
            </div>
            <div
              className="glass-card p-5 hover:border-teal/50 transition-colors cursor-pointer group"
              onClick={() => navigate("/dashboard")}
              data-testid="funnel-matters"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-500">Matters Created</span>
                  <InfoTooltip metric="matters_created" />
                </div>
                <FileCheck className="w-5 h-5 text-white" />
              </div>
              <p className="text-3xl font-bold text-white">{summary?.funnel?.matters_created || 0}</p>
              <div className="flex items-center gap-1 mt-1">
                <span className="text-xs text-slate-500 group-hover:text-white">
                  {summary?.funnel?.conversion_ob_to_matter || 0}% from OB →
                </span>
                <InfoTooltip metric="conversion_ob_matter" />
              </div>
            </div>
          </div>
          </Concertina>

          {/* Charts Row */}
          <Concertina id="charts" icon={Activity} title="Ageing & FORF Charts" defaultOpen={true}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 19.x.2 Ageing Buckets */}
            <div className="glass-card p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-white">Ageing Buckets</h3>
                    <InfoTooltip metric="ageing" />
                  </div>
                  <p className="text-sm text-slate-500">Sent/Partial onboardings by age</p>
                </div>
                <Badge variant="outline" className="bg-status-amber-dim text-status-amber border-amber-200">
                  {summary?.ageing?.total || 0} pending
                </Badge>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ageingData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a3358" />
                    <XAxis type="number" tick={{ fill: "#b4bdd6", fontSize: 12 }} />
                    <YAxis
                      dataKey="bucket"
                      type="category"
                      width={80}
                      tick={{ fill: "#b4bdd6", fontSize: 12 }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                      }}
                    />
                    <Bar dataKey="count" fill={COLORS.accent} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* 19.x.4 FORF Ready */}
            <div className="glass-card p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-white">FORF Readiness</h3>
                    <InfoTooltip metric="forf_ready" />
                  </div>
                  <p className="text-sm text-slate-500">Draft FORF created vs pending</p>
                </div>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={forfData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) => `${value}`}
                    >
                      {forfData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-6 mt-2">
                <div className="text-center">
                  <p className="text-2xl font-bold text-teal">{summary?.forf_ready?.ready || 0}</p>
                  <p className="text-xs text-slate-500">FORF Ready</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-500">{summary?.forf_ready?.not_ready || 0}</p>
                  <p className="text-xs text-slate-500">Not Ready</p>
                </div>
              </div>
            </div>
          </div>
          </Concertina>

          {/* 19.x.5 Risk Signals */}
          <Concertina id="risk-signals" icon={AlertTriangle} iconColor="text-status-red" title="Risk / Compliance Signals"
            badge={<>
              <Badge variant="outline" className="bg-purple-500/10 text-purple-300 border-purple-500/30 text-[10px]">{riskSignals?.total_overridden || 0} Overridden</Badge>
              <Badge variant="outline" className="bg-status-red/10 text-status-red border-red-500/30 text-[10px]">{riskSignals?.total_no_matter || 0} No Matter</Badge>
            </>}>

            {/* Override by Reason */}
            {riskSignals?.override_by_reason && Object.keys(riskSignals.override_by_reason).length > 0 && (
              <div className="mb-4 p-4 bg-purple-50/50 rounded-md border border-purple-100">
                <h4 className="text-sm font-medium text-purple-800 mb-2">Override Reasons</h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(riskSignals.override_by_reason).map(([reason, count]) => (
                    <Badge key={reason} variant="outline" className="bg-white text-purple-700 border-purple-200">
                      {reason}: {count}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Signals List */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {riskSignals?.signals?.slice(0, 10).map((sig, idx) => (
                <div
                  key={idx}
                  className={`flex items-center justify-between p-3 rounded-md border ${
                    sig.signal_type === "overridden"
                      ? "bg-purple-50/50 border-purple-200"
                      : "bg-status-red-dim/50 border-red-200"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <AlertTriangle
                      className={`w-4 h-4 ${
                        sig.signal_type === "overridden" ? "text-purple-600" : "text-status-red"
                      }`}
                    />
                    <div>
                      <p className="font-medium text-white">{sig.client_name}</p>
                      <p className="text-xs text-slate-500">
                        {sig.provisional_id} • {sig.fee_earner}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge
                      variant="outline"
                      className={
                        sig.signal_type === "overridden"
                          ? "bg-purple-100 text-purple-700"
                          : "bg-status-red-dim text-status-red"
                      }
                    >
                      {sig.signal_type === "overridden" ? "Overridden" : `${sig.days_overdue}d overdue`}
                    </Badge>
                    {sig.reason && (
                      <p className="text-xs text-slate-500 mt-1 max-w-48 truncate" title={sig.reason}>
                        {sig.reason}
                      </p>
                    )}
                  </div>
                </div>
              ))}
              {(!riskSignals?.signals || riskSignals.signals.length === 0) && (
                <div className="text-center py-8 text-slate-400">
                  No risk signals for selected period
                </div>
              )}
            </div>
          </Concertina>

          {/* 19.x.6 Client Experience Metrics */}
          <Concertina id="client-experience" icon={Activity} iconColor="text-teal" title="Client Experience Metrics" defaultOpen={true}>

          {/* Client Experience KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="glass-card p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-500">Median Days to Complete</span>
                <Zap className="w-5 h-5 text-teal" />
              </div>
              <p className="text-2xl font-bold text-white">
                {clientExperience?.median_days_sent_to_complete ?? "-"}
                <span className="text-base font-normal text-slate-400 ml-1">days</span>
              </p>
              <p className="text-xs text-slate-400">From sent to complete</p>
            </div>
            <div className="glass-card p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-500">P90 Days to Complete</span>
                <Clock className="w-5 h-5 text-status-amber" />
              </div>
              <p className="text-2xl font-bold text-status-amber">
                {clientExperience?.p90_days_sent_to_complete ?? "-"}
                <span className="text-base font-normal text-status-amber ml-1">days</span>
              </p>
              <p className="text-xs text-status-amber">90th percentile</p>
            </div>
            <div className="glass-card p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-500">Conflict → Sent</span>
                <ArrowRight className="w-5 h-5 text-blue-500" />
              </div>
              <p className="text-2xl font-bold text-blue-600">
                {clientExperience?.median_days_conflict_to_sent ?? "-"}
                <span className="text-base font-normal text-blue-400 ml-1">days</span>
              </p>
              <p className="text-xs text-blue-500">Median days to send OB</p>
            </div>
          </div>

          {/* Trend Chart + Entity Segments */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Trend Chart */}
            <div className="glass-card p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-white">Completion Time Trend</h3>
                  <p className="text-sm text-slate-500">Median days to complete over time</p>
                </div>
              </div>
              <div className="h-64">
                {experienceTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={experienceTrend}>
                      <defs>
                        <linearGradient id="colorMedian" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorP90" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.accent} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={COLORS.accent} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a3358" />
                      <XAxis 
                        dataKey="label" 
                        tick={{ fill: "#b4bdd6", fontSize: 11 }}
                        tickLine={false}
                      />
                      <YAxis 
                        tick={{ fill: "#b4bdd6", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        label={{ value: 'Days', angle: -90, position: 'insideLeft', fill: '#94A3B8', fontSize: 11 }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#fff",
                          border: "1px solid #E2E8F0",
                          borderRadius: "8px",
                          fontSize: "12px"
                        }}
                        formatter={(value, name) => [
                          `${value} days`,
                          name === "median_days" ? "Median" : "P90"
                        ]}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="median_days" 
                        stroke={COLORS.primary} 
                        fillOpacity={1}
                        fill="url(#colorMedian)"
                        strokeWidth={2}
                        dot={{ fill: COLORS.primary, strokeWidth: 2, r: 4 }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="p90_days" 
                        stroke={COLORS.accent} 
                        fillOpacity={1}
                        fill="url(#colorP90)"
                        strokeWidth={2}
                        dot={{ fill: COLORS.accent, strokeWidth: 2, r: 4 }}
                      />
                      <Legend 
                        formatter={(value) => value === "median_days" ? "Median" : "P90"}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400">
                    No trend data available
                  </div>
                )}
              </div>
            </div>

            {/* Entity Type Breakdown */}
            <div className="glass-card p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-white">By Entity Type</h3>
                  <p className="text-sm text-slate-500">Completion times by client type</p>
                </div>
              </div>
              <div className="space-y-3">
                {entitySegments.length > 0 ? (
                  entitySegments.map((seg, idx) => (
                    <div 
                      key={seg.segment}
                      className="flex items-center justify-between p-3 bg-navy-hover/60 rounded-md hover:bg-slate-100 cursor-pointer transition-colors"
                      onClick={() => navigateToOnboarding({ status: "complete" })}
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-3 h-3 rounded-md"
                          style={{ 
                            backgroundColor: idx === 0 ? COLORS.primary : 
                                           idx === 1 ? COLORS.secondary :
                                           idx === 2 ? COLORS.accent : COLORS.muted 
                          }}
                        />
                        <div>
                          <p className="font-medium text-white capitalize">{seg.segment}</p>
                          <p className="text-xs text-slate-500">{seg.count} records</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-white">
                          {seg.median_days ?? 0}d
                          <span className="text-slate-400 font-normal"> median</span>
                        </p>
                        <p className="text-xs text-status-amber">
                          P90: {seg.p90_days ?? 0}d
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    No segment data available
                  </div>
                )}
              </div>
            </div>
          </div>
          </Concertina>

          {/* LawBillity Billing Section */}
          {lawbillityEnabled && (
            <Concertina id="lawbillity" icon={DollarSign} iconColor="text-indigo-400" title="Billing & Time Tracking (LawBillity)" defaultOpen={true}>

              {/* Billing KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="glass-card p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-500">Total Hours</span>
                    <Timer className="w-5 h-5 text-indigo-500" />
                  </div>
                  <p className="text-2xl font-bold text-white">
                    {billingSummary?.total_hours?.toFixed(1) || 0}
                  </p>
                  <p className="text-xs text-slate-400">{billingSummary?.entry_count || 0} entries</p>
                </div>
                <div className="glass-card p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-500">Billable Hours</span>
                    <Target className="w-5 h-5 text-teal" />
                  </div>
                  <p className="text-2xl font-bold text-teal">
                    {billingSummary?.billable_hours?.toFixed(1) || 0}
                  </p>
                  <p className="text-xs text-teal">
                    {billingSummary?.total_hours > 0 
                      ? `${((billingSummary.billable_hours / billingSummary.total_hours) * 100).toFixed(0)}% billable`
                      : "0% billable"}
                  </p>
                </div>
                <div className="glass-card p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-500">Non-Billable</span>
                    <Clock className="w-5 h-5 text-slate-400" />
                  </div>
                  <p className="text-2xl font-bold text-slate-500">
                    {billingSummary?.non_billable_hours?.toFixed(1) || 0}
                  </p>
                  <p className="text-xs text-slate-400">Admin & internal</p>
                </div>
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-md border border-indigo-200 p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-indigo-600">Total Value</span>
                    <DollarSign className="w-5 h-5 text-indigo-600" />
                  </div>
                  <p className="text-2xl font-bold text-indigo-700">
                    {formatCurrency(billingSummary?.total_value)}
                  </p>
                  <p className="text-xs text-indigo-500">Billable at rate</p>
                </div>
              </div>

              {/* Utilization Table */}
              {utilizationData.length > 0 && (
                <div className="glass-card p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-white">Fee Earner Utilization</h3>
                      <p className="text-sm text-slate-500">Time tracking performance from LawBillity</p>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="utilization-table">
                      <thead>
                        <tr className="border-b border-navy-border">
                          <th className="text-left py-3 px-4 font-medium text-slate-500">Fee Earner</th>
                          <th className="text-center py-3 px-4 font-medium text-slate-500">Total Hours</th>
                          <th className="text-center py-3 px-4 font-medium text-slate-500">Billable</th>
                          <th className="text-center py-3 px-4 font-medium text-slate-500">Non-Billable</th>
                          <th className="text-center py-3 px-4 font-medium text-slate-500">Utilization</th>
                          <th className="text-right py-3 px-4 font-medium text-slate-500">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {utilizationData.map((row, idx) => (
                          <tr key={row.name} className="border-b border-navy-border hover:bg-navy-hover/60">
                            <td className="py-3 px-4 font-medium text-white">{row.name}</td>
                            <td className="py-3 px-4 text-center">{row.total_hours.toFixed(1)}</td>
                            <td className="py-3 px-4 text-center text-teal">{row.billable_hours.toFixed(1)}</td>
                            <td className="py-3 px-4 text-center text-slate-400">{row.non_billable_hours.toFixed(1)}</td>
                            <td className="py-3 px-4 text-center">
                              <Badge
                                variant="outline"
                                className={
                                  row.utilization_rate >= 75
                                    ? "bg-teal-dim text-teal border-teal"
                                    : row.utilization_rate >= 50
                                    ? "bg-status-amber-dim text-status-amber border-amber-200"
                                    : "bg-navy-hover/60 text-txt-muted border-navy-border"
                                }
                              >
                                {row.utilization_rate}%
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-right font-medium text-indigo-600">
                              {formatCurrency(row.total_value)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </Concertina>
          )}

          {/* LawBillity Not Enabled Notice */}
          {!lawbillityEnabled && (
            <div className="bg-navy-hover/60 rounded-md border border-dashed border-slate-300 p-6 text-center">
              <DollarSign className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <h3 className="font-medium text-txt-muted">Billing Analytics Coming Soon</h3>
              <p className="text-sm text-slate-400 mt-1">
                Connect LawBillity to see time tracking and billing metrics
              </p>
            </div>
          )}

          {/* Date Range Info */}
          <div className="text-center text-xs text-slate-400">
            Data from {formatDate(summary?.date_range?.start)} to {formatDate(summary?.date_range?.end)}
          </div>
        </div>
      )}
      
      {/* Conveyancing — Completions */}
      <Concertina id="conveyancing" icon={BookOpen} iconColor="text-purple-300" title="Conveyancing — Completions"
        badge={<div className="flex items-center gap-2 ml-auto" onClick={e => e.stopPropagation()}>
          <input type="file" ref={cbFileRef} accept=".xlsx,.xls" onChange={handleCBImport} className="hidden" data-testid="cb-import-file-input" />
          <div onClick={() => cbFileRef.current?.click()}
            className={`flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold rounded-md border border-purple-500/30 text-purple-300 hover:bg-purple-500/10 transition-colors cursor-pointer ${cbImporting ? "opacity-50 pointer-events-none" : ""}`}
            data-testid="cb-import-btn">
            {cbImporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            {cbImporting ? "Importing..." : "Re-import Excel"}
          </div>
          {cbImportResult && (
            <span className={`text-[10px] font-medium ${cbImportResult.success ? "text-teal" : "text-status-red"}`} data-testid="cb-import-result">
              {cbImportResult.success ? `${cbImportResult.imported} imported, ${cbImportResult.skipped} skipped` : cbImportResult.error}
            </span>
          )}
          <div className="flex gap-1 ml-2">
            {[
              { key: "today", label: "Today" },
              { key: "week", label: "This Week" },
              { key: "month", label: "This Month" },
              { key: "lastmonth", label: "Last Month" },
              { key: "year", label: "This Year" },
            ].map(r => (
              <div key={r.key} onClick={() => setCbRange(r.key)} className={`px-2.5 py-1 text-[10px] font-semibold rounded-md border transition-colors cursor-pointer ${cbRange === r.key ? "bg-purple-500/20 border-purple-500/40 text-purple-300" : "border-navy-border text-txt-muted hover:text-white"}`} data-testid={`cb-kpi-range-${r.key}`}>
                {r.label}
              </div>
            ))}
          </div>
        </div>}>
        {cbLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-purple-300" /></div>
        ) : cbMetrics ? (
          <div className="space-y-4">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <button onClick={() => navigate("/completions-book")} className="glass-card p-3 text-center hover:border-purple-400/40 transition-colors" data-testid="cb-kpi-total">
                <p className="text-2xl font-bold text-white">{cbMetrics.completed_count}</p>
                <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-0.5">Completions</p>
              </button>
              <div className="glass-card p-3 text-center">
                <p className="text-2xl font-bold text-txt-muted">{cbMetrics.total_entries}</p>
                <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-0.5">Total Entries</p>
              </div>
              <button onClick={() => navigate("/completions-book")} className={`glass-card p-3 text-center hover:border-red-400/40 transition-colors ${cbMetrics.issue_count > 0 ? "" : ""}`} data-testid="cb-kpi-issues">
                <p className={`text-2xl font-bold ${cbMetrics.issue_count > 0 ? "text-status-red" : "text-teal"}`}>{cbMetrics.issue_count}</p>
                <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-0.5">With Issues ({cbMetrics.issue_pct}%)</p>
              </button>
              <div className="glass-card p-3 text-center" data-testid="cb-kpi-coverage">
                <p className={`text-2xl font-bold ${cbMetrics.coverage_pct >= 80 ? "text-teal" : cbMetrics.coverage_pct >= 50 ? "text-status-amber" : "text-status-red"}`}>{cbMetrics.coverage_pct}%</p>
                <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-0.5">Pre-Completion Coverage</p>
              </div>
              <div className="glass-card p-3 text-center">
                <p className="text-2xl font-bold text-purple-300">{cbMetrics.by_type?.length || 0}</p>
                <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-0.5">Transaction Types</p>
              </div>
            </div>

            {/* Charts Row */}
            {/* Compact Upcoming Completions Calendar */}
            <CompactCompletionsCalendar
              data={cbCalendar}
              view={cbCalView}
              onViewChange={setCbCalView}
              onDayClick={(date) => navigate(`/completions-book?date=${date}`)}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* By Fee Earner */}
              {cbMetrics.by_fee_earner?.length > 0 && (
                <div className="glass-card p-4">
                  <h4 className="text-xs font-bold text-txt-muted mb-3 uppercase tracking-wider">Completions by Fee Earner</h4>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={cbMetrics.by_fee_earner} layout="vertical" margin={{ left: 30, right: 10 }}>
                        <XAxis type="number" tick={{ fill: "#9aa6c8", fontSize: 10 }} axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="name" tick={{ fill: "#C8C5BE", fontSize: 10 }} width={40} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #2a3358", borderRadius: 8 }} labelStyle={{ color: "#E8E5DE" }} itemStyle={{ color: "#a5b4fc" }} />
                        <Bar dataKey="count" fill="#7C3AED" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
              {/* By Transaction Type */}
              {cbMetrics.by_type?.length > 0 && (
                <div className="glass-card p-4">
                  <h4 className="text-xs font-bold text-txt-muted mb-3 uppercase tracking-wider">By Transaction Type</h4>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={cbMetrics.by_type} margin={{ left: 10, right: 10 }}>
                        <XAxis dataKey="name" tick={{ fill: "#9aa6c8", fontSize: 9 }} axisLine={false} tickLine={false} angle={-30} textAnchor="end" height={50} />
                        <YAxis tick={{ fill: "#9aa6c8", fontSize: 10 }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #2a3358", borderRadius: 8 }} labelStyle={{ color: "#E8E5DE" }} itemStyle={{ color: "#a5b4fc" }} />
                        <Bar dataKey="count" fill="#A78BFA" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            {/* Latest Issues Table */}
            {cbMetrics.latest_issues?.length > 0 && (
              <div className="glass-card p-4">
                <h4 className="text-xs font-bold text-txt-muted mb-3 uppercase tracking-wider flex items-center gap-1.5">
                  <XCircle className="w-3.5 h-3.5 text-status-red" /> Latest Entries with Issues
                </h4>
                <div className="space-y-1.5">
                  {cbMetrics.latest_issues.map(issue => (
                    <button
                      key={issue.id}
                      onClick={() => navigate(`/completions-book/${issue.id}`)}
                      className="w-full flex items-center justify-between p-2.5 rounded-md bg-navy-card border border-navy-border hover:border-red-400/30 transition-colors text-left"
                      data-testid={`cb-issue-row-${issue.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-xs text-txt-muted w-20 flex-shrink-0">{issue.completion_date}</span>
                        <span className="text-xs font-medium text-white truncate">{issue.client_name}</span>
                        <span className="text-[10px] text-txt-muted">({issue.fee_earner})</span>
                      </div>
                      <span className="text-[10px] font-bold text-status-red bg-status-red/10 border border-red-500/20 rounded px-1.5 py-0.5 flex-shrink-0">{issue.issue_count} issue{issue.issue_count !== 1 ? "s" : ""}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Fee Earner Statistics */}
            <FeeEarnerStatsSection />
          </div>
        ) : (
          <p className="text-sm text-txt-muted text-center py-4">No completions data available.</p>
        )}
      </Concertina>
      
      {/* Filing Audit Log — NEW */}
      <Concertina id="filing-audit" icon={FileText} iconColor="text-status-amber" title="Filing Audit Log" defaultOpen={false}>
        <FilingAuditLog />
      </Concertina>

      {/* Post & Scanning Stats */}
      <Concertina id="post-scanning" icon={Inbox} iconColor="text-status-amber" title="Post & Scanning Overview">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-navy-card rounded-md p-3 border border-navy-border">
            <p className="text-[10px] text-txt-muted uppercase tracking-wider">Scanned to Post</p>
            <p className="text-2xl font-bold text-txt">{postStats?.posts?.total ?? "–"}</p>
          </div>
          <div className="bg-navy-card rounded-md p-3 border border-navy-border">
            <p className="text-[10px] text-txt-muted uppercase tracking-wider">Claimed</p>
            <p className="text-2xl font-bold text-teal">{postStats?.posts?.by_status?.claimed ?? "–"}</p>
          </div>
          <div className="bg-navy-card rounded-md p-3 border border-navy-border">
            <p className="text-[10px] text-txt-muted uppercase tracking-wider">In Holding Bay</p>
            <p className="text-2xl font-bold text-status-amber">{postStats?.posts?.by_status?.pending ?? "–"}</p>
          </div>
          <div className="bg-navy-card rounded-md p-3 border border-amber-500/30">
            <p className="text-[10px] text-status-amber uppercase tracking-wider flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> 24h+ Warning
            </p>
            <p className="text-2xl font-bold text-status-amber">
              {postStats?.posts?.stale_count ?? "–"}
            </p>
            {(postStats?.posts?.stale_count || 0) > 0 && (
              <p className="text-[10px] text-status-amber/70 mt-0.5">Items unclaimed &gt; 24hrs</p>
            )}
          </div>
        </div>
        {postStats?.deadlines && (
          <div className="bg-navy-card rounded-md p-3 border border-navy-border">
            <div className="flex items-center justify-between">
              <span className="text-xs text-txt-muted">Untriaged Deadlines</span>
              <span className="text-sm font-bold text-status-red">{postStats.deadlines.pending ?? 0}</span>
            </div>
          </div>
        )}
      </Concertina>

      {/* Stale Matters */}
      <Concertina id="stale-matters" icon={AlertTriangle} iconColor="text-status-amber" title="Stale Open Matters"
        badge={
          <div className="flex items-center gap-2 ml-auto">
            {staleMatters && <span className="text-xs text-status-amber font-medium">{staleMatters.count} stale ({staleMatters.threshold_days}+ days)</span>}
            <div onClick={(e) => { e.stopPropagation(); fetchStaleMatters(); }}
              className="flex items-center gap-1 bg-navy-hover hover:bg-navy-hover text-txt-muted text-xs h-7 px-3 rounded-md cursor-pointer">
              {staleLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <><AlertTriangle className="w-3 h-3" /> Check</>}
            </div>
            <div onClick={(e) => { e.stopPropagation(); triggerStaleNudges(); }}
              className={`flex items-center gap-1 bg-status-amber hover:bg-status-amber text-white text-xs h-7 px-3 rounded-md cursor-pointer ${staleSending ? 'opacity-50 pointer-events-none' : ''}`}>
              {staleSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Send className="w-3 h-3" /> Send Nudges</>}
            </div>
          </div>
        }>
        <p className="text-xs text-txt-muted mb-3">Matters with no activity for {staleMatters?.threshold_days || 14}+ days. Click Check to load, Send Nudges to email fee earners.</p>
        {!staleMatters && !staleLoading && (
          <p className="text-xs text-txt-muted italic">Click Check to load stale matters.</p>
        )}
        {staleLoading && <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-status-amber" /></div>}
        {staleMatters && staleMatters.count === 0 && (
          <div className="bg-teal-dim/20 border border-teal/30 rounded-md p-3 text-xs text-teal">No stale matters — all open matters have recent activity.</div>
        )}
        {staleMatters && staleMatters.stale_matters?.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-txt-muted border-b border-navy-border">
                  <th className="p-2">Reference</th>
                  <th className="p-2">Client</th>
                  <th className="p-2">Fee Earner</th>
                  <th className="p-2 text-right">Days Stale</th>
                  <th className="p-2">Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {staleMatters.stale_matters.map((m, i) => (
                  <tr key={i} className="border-b border-navy-border/30 hover:bg-navy-hover/20 cursor-pointer" onClick={() => navigate(`/submission/${m.matter_id}`)}>
                    <td className="p-2 font-mono text-teal">{m.matter_reference}</td>
                    <td className="p-2 text-white">{m.client_name}</td>
                    <td className="p-2 text-txt-muted">{m.fee_earner}</td>
                    <td className="p-2 text-right">
                      <span className={`font-bold ${m.days_stale >= 30 ? 'text-status-red' : m.days_stale >= 21 ? 'text-status-amber' : 'text-yellow-300'}`}>{m.days_stale}d</span>
                    </td>
                    <td className="p-2 text-txt-muted">{m.last_activity || 'None'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Concertina>

      {/* Backfill Unfiled CCs */}
      <Concertina id="backfill" icon={FolderOpen} iconColor="text-indigo-400" title="Backfill Unfiled Conflict Checks"
        badge={<div onClick={(e) => { e.stopPropagation(); handleBackfillUnfiled(); }}
          className={`flex items-center gap-2 ml-auto bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-7 px-3 rounded-md cursor-pointer ${backfilling ? "opacity-50 pointer-events-none" : ""}`} data-testid="backfill-run-btn">
          {backfilling ? <><Loader2 className="w-3 h-3 animate-spin" /> Running...</> : <><FolderOpen className="w-3 h-3" /> Run Backfill</>}
        </div>}>
        <p className="text-xs text-txt-muted mb-2">
          Retry filing orphan conflict checks to SharePoint compliance folders.
        </p>
        {backfillResult && (
          <div className="bg-navy-card border border-navy-border rounded-md p-3 text-xs">
            <div className="flex items-center gap-3">
              <span className="text-txt-muted">Last run: {new Date(backfillResult.timestamp).toLocaleString("en-GB")}</span>
              <span className="text-teal">{backfillResult.succeeded || 0} filed</span>
              {(backfillResult.failed || 0) > 0 && <span className="text-status-red">{backfillResult.failed} failed</span>}
              <span className="text-txt-muted">{backfillResult.processed || 0} processed</span>
            </div>
          </div>
        )}
      </Concertina>

      {/* Purge Test Data */}
      <Concertina id="purge" icon={Trash2} iconColor="text-status-red" title="Purge Test Data" borderColor="border-red-500/30"
        badge={<div onClick={(e) => { e.stopPropagation(); handlePreviewTestData(); }}
          className={`flex items-center gap-2 ml-auto bg-navy-card hover:bg-navy-border text-txt-primary border border-red-500/40 text-xs h-7 px-3 rounded-md cursor-pointer ${purging ? "opacity-50 pointer-events-none" : ""}`} data-testid="purge-test-data-btn">
          {purging ? <><Loader2 className="w-3 h-3 animate-spin" /> Checking...</> : <><Trash2 className="w-3 h-3" /> Purge Test Records</>}
        </div>}>
        <p className="text-xs text-txt-muted mb-3">
          Removes records carrying a test flag: submissions, conflict checks, onboarding records and their
          documents, and test quotes with their read-receipt rows. Nothing is deleted until you have seen
          the preview and confirmed it.
        </p>

        <label className="flex items-start gap-2 text-xs text-txt-muted mb-3 cursor-pointer" data-testid="purge-include-patterns">
          <input
            type="checkbox"
            checked={purgeIncludePatterns}
            onChange={(e) => { setPurgeIncludePatterns(e.target.checked); setPurgePreview(null); setPurgeResult(null); }}
            className="mt-0.5"
          />
          <span>
            Also include older records matched only by name or email pattern, such as a TEST_ prefix.
            These are marked deleted rather than destroyed, because a pattern is not proof a record is a test.
          </span>
        </label>

        {purgePreview && (
          <div className="bg-navy-card border border-navy-border rounded-md p-3 text-xs mb-3" data-testid="purge-preview">
            <table className="w-full">
              <thead>
                <tr className="text-txt-muted text-left">
                  <th className="font-medium pb-1">Collection</th>
                  <th className="font-medium pb-1 text-right">Flagged</th>
                  <th className="font-medium pb-1 text-right">Pattern only</th>
                  <th className="font-medium pb-1 pl-4">Examples</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(purgePreview).map(([name, c]) => (
                  <tr key={name} className="border-t border-navy-border">
                    <td className="py-1 text-txt-primary">{name.replace(/_/g, " ")}</td>
                    <td className="py-1 text-right text-status-red">{c.flagged || 0}</td>
                    <td className="py-1 text-right text-txt-muted">{c.pattern_only || 0}</td>
                    <td className="py-1 pl-4 text-txt-muted">
                      {(c.flagged_samples || []).length === 0
                        ? "none"
                        : (c.flagged_samples || [])
                            .map(purgeSampleLabel)
                            .filter(Boolean)
                            .join(", ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!purgeResult && (
              <div
                onClick={handlePurgeTestData}
                className={`inline-flex items-center gap-2 mt-3 bg-status-red text-white text-xs h-7 px-3 rounded-md cursor-pointer ${purging ? "opacity-50 pointer-events-none" : ""}`}
                data-testid="purge-test-data-confirm-btn"
              >
                <Trash2 className="w-3 h-3" /> Delete these records
              </div>
            )}
          </div>
        )}

        {purgeResult && (
          <div className="bg-navy-card border border-navy-border rounded-md p-3 text-xs" data-testid="purge-result">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-status-red">{purgeResult.submissions_soft_deleted || 0} submissions deleted</span>
              <span className="text-status-red">{purgeResult.conflict_checks_deleted || 0} conflict checks deleted</span>
              <span className="text-status-red">{purgeResult.onboarding_deleted || 0} onboarding records deleted</span>
              <span className="text-status-red">{purgeResult.documents_deleted || 0} documents deleted</span>
              <span className="text-status-red">{purgeResult.quotes_deleted || 0} test quotes deleted</span>
              <span className="text-txt-muted">{purgeResult.tracking_rows_deleted || 0} read-receipt rows removed</span>
            </div>
            {(purgeResult.quote_refs_removed || []).length > 0 && (
              <p className="text-txt-muted mt-2">
                Quotes removed: {(purgeResult.quote_refs_removed || []).join(", ")}. Archived quote PDFs in
                SharePoint are not removed.
              </p>
            )}
          </div>
        )}
      </Concertina>

      {/* AI Chat Floating Button */}
      <button
        onClick={() => setChatOpen(!chatOpen)}
        className="fixed bottom-6 right-24 w-14 h-14 bg-teal hover:bg-teal-hover text-white rounded-md shadow-lg flex items-center justify-center transition-all hover:scale-105 z-40"
        data-testid="ai-chat-toggle"
      >
        {chatOpen ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
      </button>
      
      {/* AI Chat Panel */}
      {chatOpen && (
        <div className="fixed bottom-24 right-24 w-96 h-[500px] glass-card shadow-2xl flex flex-col z-40 overflow-hidden" data-testid="ai-chat-panel">
          {/* Chat Header */}
          <div className="bg-teal text-white px-4 py-3 flex items-center gap-3">
            <MessageSquare className="w-5 h-5" />
            <div>
              <h3 className="font-semibold text-sm">Analytics Assistant</h3>
              <p className="text-xs text-white/70">Ask about your metrics</p>
            </div>
          </div>
          
          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {chatMessages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-md px-3 py-2 text-sm ${
                    msg.role === "user"
                      ? "bg-teal text-white"
                      : "bg-slate-100 text-slate-700"
                  }`}
                >
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 rounded-md px-3 py-2">
                  <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
                </div>
              </div>
            )}
          </div>
          
          {/* Chat Input */}
          <div className="border-t border-slate-200 p-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendChatMessage()}
                placeholder="Ask about your data..."
                className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-teal"
                data-testid="ai-chat-input"
              />
              <Button
                size="sm"
                onClick={sendChatMessage}
                disabled={chatLoading || !chatInput.trim()}
                className="bg-teal hover:bg-teal-hover"
                data-testid="ai-chat-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 mt-2 text-center">
              Powered by AI • Ask about conversions, ageing, performance
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
